import React, { useState } from 'react';
import API from '../../API';
import { Link } from 'react-router-dom';
import GameServerManagement from './GameServerManagement/GameServerManagement';

const EditEvent = ({ event, accessToken, onEventDeleted }) => {
  const futurebot_policy_id = '2b43a53d30347c1983d632e0437e2dec5a5c00ee3007627f1f0b1d30';
  const deluxebot_policy_id = 'b65ce524203b7a7d48b55ff037c847c5ec8c185cd3bdb7abad0a02d4';

  const [title, setTitle] = useState(event.title);
  const [isDeleting, setIsDeleting] = useState(false);
  const [subtitle, setSubtitle] = useState(event.subtitle);
  const [futurebotRequired, setFuturebotRequired] = useState(
    (event.nft_required ?? []).includes(futurebot_policy_id)
  );
  const [deluxebotRequired, setDeluxebotRequired] = useState(
    (event.nft_required ?? []).includes(deluxebot_policy_id)
  );
  const [backgroundUrl, setBackgroundUrl] = useState(event.background_url);
  const [hidden, setHidden] = useState(event.hidden);

  const [contentLoadUrl, setContentLoadUrl] = useState(event.content_load_url);
  const [upcUrl, setUpcUrl] = useState(event.upc_url);
  const [umsUri, setUmsUri] = useState(event.ums_uri);
  const [upcWorldMapContentUrl, setUpcWorldMapContentUrl] = useState(
    event.upc_world_map_content_url
  );
  const [umsWorldMapContentUrl, setUmsWorldMapContentUrl] = useState(
    event.ums_world_map_content_url
  );
  const [zoomMax, setZoomMax] = useState(event.zoom_max);
  const [defaultZoomEval, setDefaultZoomEval] = useState(event.default_zoom_eval);
  const [audibleDistance, setAudibleDistance] = useState(event.audible_distance);
  const [conversationalDistance, setConversationalDistance] = useState(
    event.conversational_distance
  );

  const handleImage = async (e) => {
    const response = await API.adminGetEventBackgroundPresignedUrl(accessToken, event.id);
    if (response.status !== 200) {
      window.alert('Error getting presignedURL');
      return;
    }
    const data = await response.json();
    let selectedFile = e.target.files[0];
    const s3Response = await fetch(data.presignedUrl, {
      method: 'PUT',
      headers: {
        'Content-Type': 'image/png'
      },
      body: selectedFile
    });
    if (s3Response.status !== 200) {
      window.alert('Failed to upload the image');
      return;
    } else {
      setBackgroundUrl(s3Response.url.split('?')[0]);
    }
  };

  async function updateEvent() {
    if (accessToken) {
      let nft_required = [];
      if (futurebotRequired) {
        nft_required.push(futurebot_policy_id);
      }
      if (deluxebotRequired) {
        nft_required.push(deluxebot_policy_id);
      }

      const response = await API.adminUpdateEvent(accessToken, {
        id: event.id,
        title: title,
        subtitle: subtitle,
        background_url: backgroundUrl,
        nft_required: nft_required,
        hidden: hidden,
        content_load_url: contentLoadUrl,
        upc_url: upcUrl,
        ums_uri: umsUri,
        upc_world_map_content_url: upcWorldMapContentUrl,
        ums_world_map_content_url: umsWorldMapContentUrl,
        zoom_max: zoomMax,
        default_zoom_eval: defaultZoomEval,
        audible_distance: audibleDistance,
        conversational_distance: conversationalDistance
      });
      if (response.status !== 204) {
        window.alert('Error updating event', event.id);
        return;
      }
      window.alert('Event updated successfully!');
    }
  }

  // Helper function to scroll input to the end (show right side of long text)
  const handleInputFocus = (e) => {
    const input = e.target;
    if (input.setSelectionRange) {
      const len = input.value.length;
      input.setSelectionRange(len, len);
    }
    input.scrollLeft = input.scrollWidth;
  };

  async function deleteEvent() {
    if (
      !window.confirm(
        `Are you sure you want to delete the event "${
          event.title || event.id
        }"? This action cannot be undone.`
      )
    ) {
      return;
    }

    setIsDeleting(true);
    try {
      const response = await API.adminDeleteEvent(accessToken, { id: event.id });
      if (response.status === 204) {
        window.alert('Event deleted successfully!');
        if (onEventDeleted) {
          onEventDeleted();
        }
      } else {
        const errorText = await response.text();
        window.alert(`Failed to delete event: ${errorText || response.statusText}`);
      }
    } catch (err) {
      window.alert(`Failed to delete event: ${err.message}`);
    } finally {
      setIsDeleting(false);
    }
  }

  return (
    <div className="flex flex-col gap-6 border-2 border-white rounded-3xl p-6 bg-gray-900/50">
      {/* Event Form Section */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="flex items-center gap-3">
          <label
            htmlFor={`inputId-${event.id}`}
            className="min-w-[140px] text-right text-sm font-medium"
          >
            ID:
          </label>
          <input
            className="flex-1 px-3 py-2 rounded-lg bg-gray-800 text-white border border-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed text-right"
            id={`inputId-${event.id}`}
            type="text"
            value={event.id}
            disabled={true}
            onFocus={handleInputFocus}
          />
        </div>

        <div className="flex items-center gap-3">
          <label
            htmlFor={`inputTitle-${event.id}`}
            className="min-w-[140px] text-right text-sm font-medium"
          >
            Title:
          </label>
          <input
            className="flex-1 px-3 py-2 rounded-lg bg-gray-800 text-white border border-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500 text-right"
            id={`inputTitle-${event.id}`}
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            onFocus={handleInputFocus}
          />
        </div>

        <div className="flex items-center gap-3">
          <label
            htmlFor={`inputSubtitle-${event.id}`}
            className="min-w-[140px] text-right text-sm font-medium"
          >
            Subtitle:
          </label>
          <input
            className="flex-1 px-3 py-2 rounded-lg bg-gray-800 text-white border border-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500 text-right"
            id={`inputSubtitle-${event.id}`}
            type="text"
            value={subtitle}
            onChange={(e) => setSubtitle(e.target.value)}
            onFocus={handleInputFocus}
          />
        </div>

        <div className="flex items-center gap-3">
          <label
            htmlFor={`inputContentLoadUrl-${event.id}`}
            className="min-w-[140px] text-right text-sm font-medium"
          >
            Content Load URL:
          </label>
          <input
            className="flex-1 px-3 py-2 rounded-lg bg-gray-800 text-white border border-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500 text-right"
            id={`inputContentLoadUrl-${event.id}`}
            type="text"
            value={contentLoadUrl}
            onChange={(e) => setContentLoadUrl(e.target.value)}
            onFocus={handleInputFocus}
          />
        </div>

        <div className="flex items-center gap-3">
          <label
            htmlFor={`inputUpcUrl-${event.id}`}
            className="min-w-[140px] text-right text-sm font-medium"
          >
            UPC URL:
          </label>
          <input
            className="flex-1 px-3 py-2 rounded-lg bg-gray-800 text-white border border-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500 text-right"
            id={`inputUpcUrl-${event.id}`}
            type="text"
            value={upcUrl}
            onChange={(e) => setUpcUrl(e.target.value)}
            onFocus={handleInputFocus}
          />
        </div>

        <div className="flex items-center gap-3">
          <label
            htmlFor={`inputUmsUri-${event.id}`}
            className="min-w-[140px] text-right text-sm font-medium"
          >
            UMS URI:
          </label>
          <input
            className="flex-1 px-3 py-2 rounded-lg bg-gray-800 text-white border border-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500 text-right"
            id={`inputUmsUri-${event.id}`}
            type="text"
            value={umsUri}
            onChange={(e) => setUmsUri(e.target.value)}
            onFocus={handleInputFocus}
          />
        </div>

        <div className="flex items-center gap-3">
          <label
            htmlFor={`inputUpcWorldMapContentUrl-${event.id}`}
            className="min-w-[140px] text-right text-sm font-medium"
          >
            UPC World Map URL:
          </label>
          <input
            className="flex-1 px-3 py-2 rounded-lg bg-gray-800 text-white border border-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500 text-right"
            id={`inputUpcWorldMapContentUrl-${event.id}`}
            type="text"
            value={upcWorldMapContentUrl}
            onChange={(e) => setUpcWorldMapContentUrl(e.target.value)}
            onFocus={handleInputFocus}
          />
        </div>

        <div className="flex items-center gap-3">
          <label
            htmlFor={`inputUmsWorldMapContentUrl-${event.id}`}
            className="min-w-[140px] text-right text-sm font-medium"
          >
            UMS World Map URL:
          </label>
          <input
            className="flex-1 px-3 py-2 rounded-lg bg-gray-800 text-white border border-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500 text-right"
            id={`inputUmsWorldMapContentUrl-${event.id}`}
            type="text"
            value={umsWorldMapContentUrl}
            onChange={(e) => setUmsWorldMapContentUrl(e.target.value)}
            onFocus={handleInputFocus}
          />
        </div>

        <div className="flex items-center gap-3">
          <label
            htmlFor={`inputZoomMax-${event.id}`}
            className="min-w-[140px] text-right text-sm font-medium"
          >
            Zoom Max:
          </label>
          <input
            className="flex-1 px-3 py-2 rounded-lg bg-gray-800 text-white border border-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500 text-right"
            id={`inputZoomMax-${event.id}`}
            type="text"
            value={zoomMax}
            onChange={(e) => setZoomMax(e.target.value)}
            onFocus={handleInputFocus}
          />
        </div>

        <div className="flex items-center gap-3">
          <label
            htmlFor={`inputDefaultZoomEval-${event.id}`}
            className="min-w-[140px] text-right text-sm font-medium"
          >
            Default Zoom Eval:
          </label>
          <input
            className="flex-1 px-3 py-2 rounded-lg bg-gray-800 text-white border border-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500 text-right"
            id={`inputDefaultZoomEval-${event.id}`}
            type="text"
            value={defaultZoomEval}
            onChange={(e) => setDefaultZoomEval(e.target.value)}
            onFocus={handleInputFocus}
          />
        </div>

        <div className="flex items-center gap-3">
          <label
            htmlFor={`inputAudibleDistance-${event.id}`}
            className="min-w-[140px] text-right text-sm font-medium"
          >
            Audible Distance:
          </label>
          <input
            className="flex-1 px-3 py-2 rounded-lg bg-gray-800 text-white border border-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500 text-right"
            id={`inputAudibleDistance-${event.id}`}
            type="text"
            value={audibleDistance}
            onChange={(e) => setAudibleDistance(e.target.value)}
            onFocus={handleInputFocus}
          />
        </div>

        <div className="flex items-center gap-3">
          <label
            htmlFor={`inputConversationalDistance-${event.id}`}
            className="min-w-[140px] text-right text-sm font-medium"
          >
            Conversational Distance:
          </label>
          <input
            className="flex-1 px-3 py-2 rounded-lg bg-gray-800 text-white border border-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500 text-right"
            id={`inputConversationalDistance-${event.id}`}
            type="text"
            value={conversationalDistance}
            onChange={(e) => setConversationalDistance(e.target.value)}
            onFocus={handleInputFocus}
          />
        </div>

        <div className="flex items-center gap-3">
          <label
            htmlFor={`inputBackgroundUrl-${event.id}`}
            className="min-w-[140px] text-right text-sm font-medium"
          >
            Upload Image:
          </label>
          <input
            className="flex-1 px-3 py-2 rounded-lg bg-gray-800 text-white border border-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-blue-600 file:text-white hover:file:bg-blue-700 file:cursor-pointer"
            id={`inputBackgroundUrl-${event.id}`}
            type="file"
            accept="image/*"
            onChange={handleImage}
          />
        </div>

        <div className="flex items-center gap-3 hidden">
          <label
            htmlFor={`inputFuturebotRequired-${event.id}`}
            className="min-w-[140px] text-right text-sm font-medium"
          >
            Futurebots are tickets:
          </label>
          <input
            className="w-5 h-5 rounded border-gray-700 bg-gray-800 text-blue-600 focus:ring-2 focus:ring-blue-500"
            id={`inputFuturebotRequired-${event.id}`}
            type="checkbox"
            checked={futurebotRequired}
            onChange={(e) => setFuturebotRequired(e.target.checked)}
          />
        </div>

        <div className="flex items-center gap-3 hidden">
          <label
            htmlFor={`inputDeluxebotRequired-${event.id}`}
            className="min-w-[140px] text-right text-sm font-medium"
          >
            Deluxebots are tickets:
          </label>
          <input
            className="w-5 h-5 rounded border-gray-700 bg-gray-800 text-blue-600 focus:ring-2 focus:ring-blue-500"
            id={`inputDeluxebotRequired-${event.id}`}
            type="checkbox"
            checked={deluxebotRequired}
            onChange={(e) => setDeluxebotRequired(e.target.checked)}
          />
        </div>

        <div className="flex items-center gap-3">
          <label
            htmlFor={`inputHidden-${event.id}`}
            className="min-w-[140px] text-right text-sm font-medium"
          >
            Private:
          </label>
          <input
            className="w-5 h-5 rounded border-gray-700 bg-gray-800 text-blue-600 focus:ring-2 focus:ring-blue-500"
            id={`inputHidden-${event.id}`}
            type="checkbox"
            checked={hidden}
            onChange={(e) => setHidden(e.target.checked)}
          />
        </div>
      </div>

      {/* Update/Delete Event Buttons */}
      <div className="flex justify-center gap-3 mt-4">
        <button className="ff-button text-sm px-4 py-2" onClick={updateEvent}>
          Update Event
        </button>
        <button
          className="ff-button text-sm px-4 py-2 disabled:opacity-50 disabled:cursor-not-allowed"
          onClick={deleteEvent}
          disabled={isDeleting}
          style={{ backgroundColor: '#dc2626' }}
        >
          {isDeleting ? 'Deleting...' : 'Delete Event'}
        </button>
      </div>

      {/* Game Server Management (EC2 Instances) */}
      <GameServerManagement eventId={event.id} accessToken={accessToken} />

      {/* Action Buttons */}
      <div className="flex justify-center mt-4">
        <Link className="ff-button text-sm px-4 py-2" to={`/events/${event.id}`}>
          Enter Event
        </Link>
      </div>
    </div>
  );
};

export default EditEvent;
